/**
 * Bill Deposit Page
 *
 * Mobile-first page for adding deposits to credit bills.
 */

import { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Receipt, CreditCard, Building, Banknote, Smartphone, FileCheck } from 'lucide-react'

import { useBill, useAddDeposit } from '@/hooks/useBills'
import { formatCurrency, formatDate } from '@/lib/utils'
import { t } from '@/i18n'
import { PaymentType } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toaster'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const createSchema = () =>
  z.object({
    amount: z.number().positive(t('validation.amountMustBeGreater')),
    payment_type: z.nativeEnum(PaymentType),
    notes: z.string().optional(),
  })

type FormValues = z.infer<ReturnType<typeof createSchema>>

const paymentTypes = [
  { value: PaymentType.CASH, labelKey: 'billing.cash', icon: Banknote },
  { value: PaymentType.CARD, labelKey: 'billing.card', icon: CreditCard },
  { value: PaymentType.TRANSFER, labelKey: 'billing.transfer', icon: Building },
  { value: PaymentType.SINPE, labelKey: 'payment.sinpe', icon: Smartphone },
  { value: PaymentType.CHECK, labelKey: 'payment.check', icon: FileCheck },
]

export function BillDepositPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const billId = id && !isNaN(Number(id)) ? Number(id) : 0

  const { data: bill, isLoading } = useBill(billId)
  const addDepositMutation = useAddDeposit()

  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) {
      navigate(-1)
    } else {
      navigate(`/bills/${billId}`)
    }
  }

  const remainingDebt = bill ? Math.round((bill.total - (bill.total_deposited || 0)) * 100) / 100 : 0

  const form = useForm<FormValues>({
    resolver: zodResolver(createSchema()),
    defaultValues: {
      amount: 0,
      payment_type: PaymentType.CASH,
      notes: '',
    },
  })

  // Update default amount when bill loads (only once)
  const initializedRef = useRef(false)
  useEffect(() => {
    if (bill && !initializedRef.current) {
      initializedRef.current = true
      form.setValue('amount', remainingDebt)
    }
  }, [bill, remainingDebt, form])

  const watchedAmount = form.watch('amount') || 0
  const afterDeposit = Math.round(Math.max(0, remainingDebt - watchedAmount) * 100) / 100

  const onSubmit = async (data: FormValues) => {
    if (data.amount > remainingDebt) {
      form.setError('amount', {
        message: `${t('validation.amountCannotExceedDebt')} (${formatCurrency(remainingDebt)})`,
      })
      return
    }

    try {
      const deposit = await addDepositMutation.mutateAsync({
        billId,
        data: {
          amount: data.amount,
          payment_type: data.payment_type,
          notes: data.notes,
        },
      })
      toast({ title: t('bills.depositAdded') })
      // Redirect to the deposit detail/result page (has a print action).
      navigate(`/bills/${billId}/deposits/${deposit.id}`)
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('bills.depositError'),
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!bill) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Receipt className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('bills.billNotFound')}</p>
        <Button variant="outline" onClick={() => navigate('/bills')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('bills.backToBills')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={goBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">
            {t('bills.addDeposit')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('bills.billNumber')}: {bill.bill_number || `#${bill.id}`}
          </p>
        </div>
      </div>

      {/* Bill Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            {t('bills.billInfo')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.client')}</span>
            <span className="font-medium">{bill.client_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.date')}</span>
            <span className="font-medium">{formatDate(bill.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.total')}</span>
            <span className="font-medium">{formatCurrency(bill.total)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Deposit Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('bills.remainingDebt')}</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {formatCurrency(remainingDebt)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-950/20 border-green-100 dark:border-green-900/30">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('bills.afterDeposit')}</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(afterDeposit)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Deposit Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              {/* Amount */}
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('bills.amount')} *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          ₡
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={remainingDebt}
                          placeholder="0"
                          inputMode="decimal"
                          className="pl-8 h-14 text-2xl font-bold"
                          value={field.value === 0 ? '' : field.value}
                          onChange={(e) => {
                            const val = e.target.value
                            field.onChange(val === '' ? 0 : parseFloat(val))
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Quick Amount Buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => form.setValue('amount', remainingDebt)}
                >
                  {t('payment.exact')} ({formatCurrency(remainingDebt)})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => form.setValue('amount', Math.ceil(remainingDebt / 2))}
                >
                  50%
                </Button>
              </div>

              {/* Payment Type */}
              <FormField
                control={form.control}
                name="payment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('bills.paymentMethod')} *</FormLabel>
                    <FormControl>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {paymentTypes.map((type) => {
                          const Icon = type.icon
                          return (
                            <Button
                              key={type.value}
                              type="button"
                              variant={field.value === type.value ? 'default' : 'outline'}
                              className="h-14 flex flex-col items-center gap-1"
                              onClick={() => field.onChange(type.value)}
                            >
                              <Icon className="h-5 w-5" />
                              <span className="text-xs">{t(type.labelKey)}</span>
                            </Button>
                          )
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('bills.notes')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('bills.notesPlaceholder')}
                        className="resize-none"
                        rows={2}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 sm:flex-1"
              onClick={goBack}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              className="h-12 sm:flex-1"
              disabled={addDepositMutation.isPending || watchedAmount <= 0}
            >
              {addDepositMutation.isPending && <Spinner size="sm" className="mr-2" />}
              {addDepositMutation.isPending
                ? t('common.saving')
                : `${t('bills.addDeposit')} ${formatCurrency(watchedAmount)}`}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
